import chunk from "lodash/chunk";
import { flattenObject } from "./object";
import type { EventData, GA4EventParameters } from "./google-analytics-4.type";

export const COMMON_KEYS = [
	"eventId",
	"instanceId",
	"calculationId",
	"buttonId",
	"leadUuid",
	"productTypes",
	"componentType",
	"componentName",
	"isSignedIn",
	"variant",
	"send_to",
];

const MAX_EVENT_PARAMETERS = 25;
const MAX_PARAMETER_LENGTH = 100;
const MAX_ITEMS = 200;

let eventCounter = 0;
const getUniqueEventId = () => {
	const date = Date.now();
	const random = 1_000_000 + Math.floor(Math.random() * 8_999_999);

	return `${date}${random}${eventCounter++}`;
};

export function serializeModel(model: GA4EventParameters): GA4EventParameters {
	if (!model) {
		return {};
	}

	return Object.entries(flattenObject(model))
		.filter(([, value]) => value !== undefined && value !== null)
		.reduce((acc, [key, value]) => {
			const newKey = key.split(".").join("__");

			if (typeof value === "string") {
				if (value !== "" && !isNaN(Number(value))) {
					acc[newKey] = Number(value);

					return acc;
				}

				// Split long strings into multiple parameters
				if (value.length > MAX_PARAMETER_LENGTH) {
					const chunks = chunk(value, MAX_PARAMETER_LENGTH);

					chunks.forEach((chunkValue, index) => {
						acc[`${newKey}#${index}`] = chunkValue.join("");
					});

					return acc;
				}
			}

			acc[newKey] = value;

			return acc;
		}, {});
}

export function splitEventParameters(event: string, eventModel: GA4EventParameters): EventData[] {
	const { items = [], user_data, ...rawModel } = eventModel;

	const fullModel = Object.entries(serializeModel(rawModel));
	const commonParameters = Object.fromEntries(fullModel.filter(([key]) => COMMON_KEYS.includes(key)));
	const specificParameters = {
		eventId: 0, // This will be replaced by getUniqueEventId
		items,
		user_data: normalizeUserData(user_data),
		...Object.fromEntries(fullModel.filter(([key]) => !COMMON_KEYS.includes(key))),
	};

	if (!items?.length) {
		delete specificParameters.items;
	}

	if (!user_data) {
		delete specificParameters.user_data;
	}

	const customParameterPerRequest = MAX_EVENT_PARAMETERS - Object.keys(commonParameters).length;
	const eventModelChunks = chunk(Object.entries(specificParameters), customParameterPerRequest);

	const eventList = eventModelChunks.map((eventModelChunk, index) => ({
		event: index === 0 ? event : `${event}_extra${index}`,
		eventModel: {
			...commonParameters,
			...Object.fromEntries(eventModelChunk),
			eventId: getUniqueEventId(),
		} as Record<string, unknown>,
	}));

	if (items?.length > MAX_ITEMS) {
		const itemChunks = chunk(items, MAX_ITEMS);

		eventList[0].eventModel.items = itemChunks[0];

		return eventList.concat(
			itemChunks.slice(1).map((itemChunk) => ({
				event,
				eventModel: {
					...commonParameters,
					items: itemChunk,
					eventId: getUniqueEventId(),
				},
			})),
		);
	}

	if (!eventList?.length) {
		return [
			{
				event,
				eventModel: {
					...commonParameters,
					eventId: getUniqueEventId(),
				},
			},
		];
	}

	return eventList;
}

const normalizeUserData = (userData: Record<string, unknown>): Record<string, unknown> => {
	if (!userData) {
		return userData;
	}

	const { email_address, phone_number, ...rest } = userData;

	return {
		...(email_address && { email_address: email_address.toString().toLowerCase().trim() }),
		...(phone_number && { phone_number: phone_number.toString().replace(/[^\d]/g, "") }),
		...rest,
	};
};
